import { createContext, useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where
} from 'firebase/firestore';
import { hasAccess } from '../lib/modules';

export const EntradasContext = createContext();

const CACHE_TTL_MS = 20 * 60 * 1000; // 20 minutos
const buildCacheKey = (userEmail, filtros) =>
  `entradas:list:${userEmail || 'anon'}:${filtros?.fecha || ''}:${filtros?.proveedor || ''}:${filtros?.tipo || ''}:${filtros?.estado || ''}`;

const cacheGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!ts || Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
};

const cacheSet = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch {}
};

export const EntradasProvider = ({ children, currentUser }) => {
  const [entradas, setEntradas] = useState([]);
  const [loadingEntradas, setLoadingEntradas] = useState(true);
  const [paginaActual, setPaginaActual] = useState(1);
  const [ultimosDocumentos, setUltimosDocumentos] = useState([]);
  const [hasMoreEntradas, setHasMoreEntradas] = useState(true);
  const entradasPorPagina = 20;
  const ultimosDocsRef = useRef([]);
  const filtrosRef = useRef({ key: "{}", valores: {} });

  const cargarEntradas = async (pagina = 1, filtros = {}, options = { force: false, rebuild: false }) => {
    setLoadingEntradas(true);
    try {
      const { force = false, rebuild = false } = options || {};
      const filtroFecha = (filtros.fecha || '').trim();
      const filtroProveedor = (filtros.proveedor || '').trim();
      const filtroTipo = (filtros.tipo || '').trim();
      const filtroEstado = (filtros.estado || '').trim();
      const filtroKey = JSON.stringify({ fecha: filtroFecha, proveedor: filtroProveedor, tipo: filtroTipo, estado: filtroEstado });

      // Si cambiaron los filtros o es la primera página, resetea cursores
      if (pagina === 1 || filtroKey !== filtrosRef.current.key) {
        filtrosRef.current = { key: filtroKey, valores: { fecha: filtroFecha, proveedor: filtroProveedor, tipo: filtroTipo, estado: filtroEstado } };
        ultimosDocsRef.current = [];
        setUltimosDocumentos([]);
      }
      if (!currentUser) {
        setEntradas([]);
        setPaginaActual(1);
        setUltimosDocumentos([]);
        setHasMoreEntradas(false);
        setLoadingEntradas(false);
        return;
      }

      const canRead = hasAccess(currentUser, 'incoming');
      if (!canRead) {
        setEntradas([]);
        setPaginaActual(1);
        setUltimosDocumentos([]);
        setHasMoreEntradas(false);
        setLoadingEntradas(false);
        return;
      }

      let q;
      const baseCol = collection(db, "entradas");
      const clauses = [];
      if (filtroFecha) clauses.push(where("registrationDate", "==", filtroFecha));
      if (filtroProveedor) clauses.push(where("supplier", "==", filtroProveedor));
      if (filtroTipo) clauses.push(where("entryType", "==", filtroTipo));
      if (filtroEstado) clauses.push(where("paymentStatus", "==", filtroEstado));
      clauses.push(orderBy("timestamp", "desc"));

      const cKey = buildCacheKey(currentUser.email, filtrosRef.current.valores);

      if (pagina === 1 && !force) {
        const cached = cacheGet(cKey);
        if (cached) {
          setEntradas(cached.data || []);
          setPaginaActual(1);
          setHasMoreEntradas(Boolean(cached.hasMore));
          setLoadingEntradas(false);
          // reconstruye cursores en segundo plano para permitir paginaciÃ³n
          if (cached.hasMore) cargarEntradas(1, filtros, { force: true, rebuild: true });
          return;
        }
      }

      if (pagina === 1) {
        setHasMoreEntradas(true);
        q = query(baseCol, ...clauses, limit(entradasPorPagina));
      } else {
        const ultimoDoc = ultimosDocsRef.current[pagina - 2] || ultimosDocumentos[pagina - 2];
        if (!ultimoDoc) {
          console.warn(`[Entradas] sin cursor para pagina ${pagina}. ultimos len: ${ultimosDocsRef.current.length}`);
          // reconstruir cursores desde pÃ¡gina 1
          await cargarEntradas(1, filtros, { force: true, rebuild: true });
          const rebuilt = ultimosDocsRef.current[pagina - 2];
          if (!rebuilt) {
            setLoadingEntradas(false);
            return;
          }
          q = query(baseCol, ...clauses, startAfter(rebuilt), limit(entradasPorPagina));
        } else {
          q = query(baseCol, ...clauses, startAfter(ultimoDoc), limit(entradasPorPagina));
        }
      }

      const snapshot = await getDocs(q);
      const docs = snapshot.docs;

      if (docs.length > 0) {
        const cursor = docs[docs.length - 1];
        ultimosDocsRef.current[pagina - 1] = cursor;
        const nuevosUltimos = [...ultimosDocsRef.current];
        setUltimosDocumentos(nuevosUltimos);
      }

      const data = docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      const hasMore = docs.length > 0 && docs.length === entradasPorPagina;
      setHasMoreEntradas(hasMore);

      setEntradas(data);
      setPaginaActual(pagina);

      if (pagina === 1 && !rebuild) {
        cacheSet(cKey, { data, hasMore });
      }
    } catch (error) {
      console.error("🚫 Error al cargar entradas paginadas:", error);
      setHasMoreEntradas(false);
    }
    setLoadingEntradas(false);
  };

  useEffect(() => {
    // Las entradas se consultan al abrir el modulo. Evita gastar lecturas
    // durante el inicio de sesion cuando el usuario no necesita esta vista.
    if (!currentUser || !hasAccess(currentUser, 'incoming')) {
      setEntradas([]);
      setPaginaActual(1);
      setUltimosDocumentos([]);
      setHasMoreEntradas(false);
      setLoadingEntradas(false);
    }
  }, [currentUser]);

  return (
    <EntradasContext.Provider value={{
      entradas,
      setEntradas,
      loadingEntradas,
      setLoadingEntradas,
      cargarEntradas,
      paginaActual,
      hasMoreEntradas
    }}>
      {children}
    </EntradasContext.Provider>
  );
};
